
/**
 * Shared hook for tournament result management
 * Works with both Mexicano and Americano tournaments through adapters
 */

import { useState, useEffect } from 'react';
import { BaseResult, TournamentAdapter, UseTournamentResults } from '@/types/shared-tournament';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

export const useTournamentResults = (
  adapter: TournamentAdapter,
  currentRound: number
): UseTournamentResults => {
  const { language } = useLanguage();
  const config = adapter.getConfig();
  
  const [totalPoints, setTotalPoints] = useState(config.defaultPoints);
  const [isRestoringScores, setIsRestoringScores] = useState(false);
  const [results, setResults] = useState<BaseResult[]>([]);

  // Initialize results when tournament data changes
  useEffect(() => {
    const matches = adapter.getMatches();
    
    // Check if we have stored results for the current round
    const restoredResults = adapter.restoreResults(currentRound);
    
    if (restoredResults && restoredResults.length > 0) {
      // Set flag to prevent totalPoints effect from overwriting during restoration
      setIsRestoringScores(true);
      
      console.log('Restoring results from adapter:', restoredResults);
      
      // Use restored results exactly as they are - they now include player information
      const convertedResults = restoredResults.map((result, index) => {
        // Handle different score formats
        let score: [number, number];
        let calculatedTotal = config.defaultPoints;
        
        if (result.score) {
          if (Array.isArray(result.score)) {
            score = result.score;
            calculatedTotal = score[0] + score[1];
          } else if (result.score && typeof result.score === 'object' && 'team1' in result.score && 'team2' in result.score) {
            const americanoScore = result.score as { team1: number; team2: number };
            score = [americanoScore.team1, americanoScore.team2];
            calculatedTotal = americanoScore.team1 + americanoScore.team2;
          } else {
            score = [Math.floor(config.defaultPoints / 2), Math.floor(config.defaultPoints / 2)];
          }
        } else {
          score = [Math.floor(config.defaultPoints / 2), Math.floor(config.defaultPoints / 2)];
        }
        
        return {
          court: index,
          // Preserve all original data from restored results
          winner: result.winner || (score[0] > score[1] ? "team1" : "team2") as "team1" | "team2",
          selectedTeam: result.selectedTeam || "team1" as "team1" | "team2",
          score,
          // Preserve player information if available
          players: result.players
        };
      });
      
      setResults(convertedResults);
      
      // Set total points based on the first result's score total
      if (convertedResults.length > 0 && convertedResults[0].score) {
        const calculatedTotal = convertedResults[0].score[0] + convertedResults[0].score[1];
        setTotalPoints(calculatedTotal);
        console.log('Set total points from restored results:', calculatedTotal);
      } else {
        setTotalPoints(config.defaultPoints);
      }
      
      // Clear the flag after restoration is complete
      setTimeout(() => setIsRestoringScores(false), 0);
    } else {
      // Initialize with fresh results for new rounds
      setIsRestoringScores(false);
      setResults(
        matches.map((match, index) => ({
          court: index,
          winner: "team1",
          selectedTeam: "team1",
          score: [
            Math.floor(config.defaultPoints / 2),
            Math.floor(config.defaultPoints / 2)
          ],
          // Include player information from current matches - convert to string format
          players: {
            team1: {
              player1: match.team1.player1.name,
              player2: match.team1.player2.name
            },
            team2: {
              player1: match.team2.player1.name,
              player2: match.team2.player2.name
            }
          }
        }))
      );
      setTotalPoints(config.defaultPoints);
    }
  }, [currentRound, adapter, config.defaultPoints]);

  // Update results when total points change (but not during restoration)
  useEffect(() => {
    if (isRestoringScores) {
      return;
    }
    
    setResults(prev => 
      prev.map(result => {
        const currentTotal = result.score[0] + result.score[1];
        let team1Score, team2Score;
        
        if (currentTotal === 0) {
          // If no scores yet, split evenly
          team1Score = Math.floor(totalPoints / 2);
          team2Score = totalPoints - team1Score;
        } else {
          // Maintain the ratio of existing scores
          const team1Ratio = result.score[0] / currentTotal;
          team1Score = Math.round(team1Ratio * totalPoints);
          team2Score = totalPoints - team1Score;
        }
        
        return {
          ...result,
          score: [team1Score, team2Score],
          winner: team1Score > team2Score ? "team1" : "team2"
        };
      })
    );
  }, [totalPoints, isRestoringScores]);

  const updateScore = (courtIndex: number, score: number) => {
    setResults(prev => 
      prev.map((result, idx) => {
        if (idx === courtIndex) {
          let newScore: [number, number];
          
          if (result.selectedTeam === "team1") {
            newScore = [score, totalPoints - score];
          } else {
            newScore = [totalPoints - score, score];
          }
          
          return { 
            ...result, 
            score: newScore,
            winner: newScore[0] > newScore[1] ? "team1" : "team2"
          };
        }
        return result;
      })
    );
  };

  const toggleSelectedTeam = (courtIndex: number) => {
    setResults(prev => 
      prev.map((result, idx) => {
        if (idx === courtIndex) {
          const newSelectedTeam = result.selectedTeam === "team1" ? "team2" : "team1";
          return { 
            ...result, 
            selectedTeam: newSelectedTeam
          };
        }
        return result;
      })
    );
  };

  const handleSubmit = () => {
    adapter.submitResults(results);
    toast.success(language === 'no' ? 'Resultater lagret!' : 'Results saved!');
  };

  return {
    results,
    totalPoints,
    isRestoringScores,
    setTotalPoints,
    updateScore,
    toggleSelectedTeam,
    handleSubmit
  };
};
